import React, { useEffect } from "react";

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import {
  CloudDownload,
  CloudUpload,
  CreateNewFolder,
  Download,
} from "@mui/icons-material";
import ContextElementFunctions from "../../Contexts/ContextElementFunctions";
import ContextViewFileList from "../../Contexts/ContextViewFileList";
import { downloadFile, downloadFolder } from "./background_tasks";
import { DocumentsService } from "../../../services/openapi";
import APIWrapperContext from "../../../services/APIWrapperContext";
import { Idir } from "../../../Application/MainPages/Interfaces/interfaces";

interface IFolderTemplate {
  key: string;
  label: string;
  description: string;
}

export const AddFolder = () => {
  const {call_backend_api} = React.useContext(APIWrapperContext);
  const { dir_id, type, name } = React.useContext(ContextElementFunctions);
  const { update_dir_view, invalidate_dir_cache, curr_dir_id } = React.useContext(ContextViewFileList);
  const [openNewFolderDialog, setOpenNewFolderDialog] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [templates, setTemplates] = React.useState<IFolderTemplate[]>([]);
  const [template, setTemplate] = React.useState<string>("");
  const createFolder = () => {
    setOpenNewFolderDialog(true);
    // Fetched on open, not on mount: this component is rendered once per folder row.
    call_backend_api(
      DocumentsService.listFolderTemplatesDocFolderTemplatesGet(),
      (res: IFolderTemplate[]) => setTemplates(Array.isArray(res) ? res : []),
      () => setTemplates([]),
      undefined,
      false
    );
  };

  const handleCreateFolder = () => {
      const trimmedFolderName = newFolderName.trim();
      if(trimmedFolderName && dir_id !== undefined && dir_id !== null){
        setLoading(true);
        call_backend_api(
          DocumentsService.createFolderDocCreateFolderPost(trimmedFolderName, dir_id, template || undefined),
          (res: Idir) => {
            setLoading(false);
            setOpenNewFolderDialog(false);
            setNewFolderName("");
            setTemplate("");
            invalidate_dir_cache();
            // At root, the context curr_dir_id is undefined; fall back to dir_id ('')
            // so the refresh targets root.
            update_dir_view(curr_dir_id ?? dir_id);
          },
          () => {
            setLoading(false);
          },
          undefined,
          true,
          "Ordner erstellt!",
          "Ordner konnte nicht erstellt werden!"
        );
      }
    };

  const closeNewFolderDialog = () => {
    if(loading){
      return;
    }
    setOpenNewFolderDialog(false);
    setNewFolderName("");
    setTemplate("");
  };


  return (
    <>
    { (type === "dir") &&
      <>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          createFolder();
        }}
        title={"Ordner erstellen"}
      >
        <CreateNewFolder />
      </IconButton>
      <Dialog open={openNewFolderDialog} onClose={loading ? undefined : closeNewFolderDialog} onClick={(e)=>{e.stopPropagation()}}>
         <DialogTitle>Neuer Ordner in {name}</DialogTitle>
         <DialogContent>
           <TextField
             autoFocus
             margin="dense"
             id="name"
             label="Neuer Ordnername"
             type="text"
             fullWidth
             onChange={(e) => {setNewFolderName(e.target.value)}}
             variant="standard"
             disabled={loading}
           />
           {templates.length > 0 && (
             <FormControl fullWidth margin="dense" variant="standard" disabled={loading}>
               <InputLabel id="folder-template-label">Vorlage</InputLabel>
               <Select
                 labelId="folder-template-label"
                 value={template}
                 onChange={(e) => setTemplate(String(e.target.value))}
               >
                 <MenuItem value="">Keine Vorlage (leerer Ordner)</MenuItem>
                 {templates.map((t) => (
                   <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                 ))}
               </Select>
               {template && (
                 <FormHelperText>{templates.find((t) => t.key === template)?.description}</FormHelperText>
               )}
             </FormControl>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={closeNewFolderDialog} disabled={loading}>Abbrechen</Button>
           <Button
             onClick={handleCreateFolder}
             variant="contained"
             disabled={loading || !newFolderName.trim()}
             aria-busy={loading}
             startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
             sx={{minWidth: 128}}>
             {loading ? 'Wird erstellt…' : 'Erstellen'}
           </Button>
         </DialogActions>
       </Dialog>
      </>}
    </>
  );
};
